/**
 * Infinite War spell → authored VFX preset map (Phaser-free).
 * Present layer resolves ids; sim only emits spell_fx / shockwave events.
 */

import type { IwTeam } from "../sim/types";

/** Destruction gimmicks that emit readable cast VFX (excludes summon). */
export const IW_SPELL_GIMMICKS = [
  "shockwave",
  "cone",
  "zone",
  "delayed_blast",
  "curse_cluster",
  "sacrifice_nuke",
  "board_half",
] as const;

export type IwSpellGimmick = (typeof IW_SPELL_GIMMICKS)[number];

export type IwSpellFxPhase = "telegraph" | "impact";

export type IwSpellFaction = "celestial" | "death";

export type IwSpellVfxResolved = {
  /** Primary punch / telegraph cue. */
  primary: string;
  /** Soft linger after impact (mire, blight residue). */
  residual?: string;
};

export function isIwSpellGimmick(spell: string): spell is IwSpellGimmick {
  return (IW_SPELL_GIMMICKS as readonly string[]).includes(spell);
}

export function spellFaction(team: IwTeam): IwSpellFaction {
  return team === "south" ? "celestial" : "death";
}

/**
 * Map a destruction spell + team + phase → preset ids.
 * delayed_blast: cast → telegraph, detonate → impact.
 * Other spells default to impact (instant resolve).
 */
export function resolveIwSpellVfx(
  spell: IwSpellGimmick,
  team: IwTeam,
  phase: IwSpellFxPhase = "impact",
): IwSpellVfxResolved {
  const fac = spellFaction(team);
  if (spell === "delayed_blast") {
    return {
      primary:
        phase === "telegraph"
          ? `iw.spell.delayed_blast.telegraph.${fac}`
          : `iw.spell.delayed_blast.impact.${fac}`,
    };
  }
  if (spell === "zone") {
    return {
      primary: `iw.spell.zone.${fac}`,
      residual: `iw.spell.zone.residual.${fac}`,
    };
  }
  if (spell === "curse_cluster") {
    return {
      primary: `iw.spell.curse_cluster.${fac}`,
      residual: `iw.spell.curse_cluster.residual.${fac}`,
    };
  }
  return { primary: `iw.spell.${spell}.${fac}` };
}

/** Every preset id the map can emit — for vitest completeness. */
export function listIwSpellPresetIds(): string[] {
  const ids = new Set<string>();
  for (const spell of IW_SPELL_GIMMICKS) {
    for (const team of ["south", "north"] as const) {
      if (spell === "delayed_blast") {
        for (const phase of ["telegraph", "impact"] as const) {
          const r = resolveIwSpellVfx(spell, team, phase);
          ids.add(r.primary);
          if (r.residual) ids.add(r.residual);
        }
      } else {
        const r = resolveIwSpellVfx(spell, team, "impact");
        ids.add(r.primary);
        if (r.residual) ids.add(r.residual);
      }
    }
  }
  return [...ids].sort();
}
